/* Field — Daylight scroll engine.
 * Dependency-free. Powers:
 *   [data-fd-reveal]        — IntersectionObserver reveal (adds .fd-in)
 *   [data-fd-stagger]       — staggers direct [data-fd-reveal] descendants
 *   [data-fd-scene]         — sticky pinned scenes; sets --fd-progress 0→1
 *   [data-fd-words]         — wraps words in .fd-w spans for progressive reveal
 *   [data-fd-parallax="x"]  — translateY parallax at factor x
 * Honors prefers-reduced-motion and the Shopify theme editor (design mode):
 * both get everything fully revealed via the .fd-no-motion class.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = document.documentElement.classList.contains('shopify-design-mode');

  if (reduced || designMode) {
    document.documentElement.classList.add('fd-no-motion');
  }

  /* ---- Word wrapping (runs regardless, so layout is stable) ------------- */
  function wrapWords(el) {
    if (el.dataset.fdWordsReady) return;
    el.dataset.fdWordsReady = 'true';
    var index = 0;

    function walk(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        var parts = node.textContent.split(/(\s+)/);
        var frag = document.createDocumentFragment();
        parts.forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            var span = document.createElement('span');
            span.className = 'fd-w';
            span.style.setProperty('--fd-i', index++);
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('fd-w')) {
        Array.prototype.slice.call(node.childNodes).forEach(walk);
      }
    }

    Array.prototype.slice.call(el.childNodes).forEach(walk);
    el.style.setProperty('--fd-n', index);
  }

  function initWords(root) {
    (root || document).querySelectorAll('[data-fd-words]').forEach(wrapWords);
  }

  /* ---- Reveal on scroll -------------------------------------------------- */
  var io = null;

  function initReveals(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-fd-stagger]').forEach(function (parent) {
      var items = parent.querySelectorAll('[data-fd-reveal]');
      items.forEach(function (item, i) {
        item.style.setProperty('--fd-delay', i * 0.09 + 's');
      });
    });

    if (reduced || designMode) return;

    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('fd-in');
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    scope.querySelectorAll('[data-fd-reveal]:not(.fd-in)').forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---- Scenes + parallax (rAF driven) ------------------------------------ */
  var scenes = [];
  var parallaxEls = [];
  var ticking = false;

  function collect() {
    scenes = Array.prototype.slice.call(document.querySelectorAll('[data-fd-scene]'));
    parallaxEls = Array.prototype.slice.call(document.querySelectorAll('[data-fd-parallax]'));
  }

  function update() {
    ticking = false;
    var vh = window.innerHeight;

    scenes.forEach(function (scene) {
      var rect = scene.getBoundingClientRect();
      var total = rect.height - vh;
      if (total <= 0) {
        scene.style.setProperty('--fd-progress', 1);
        return;
      }
      var progress = Math.min(1, Math.max(0, -rect.top / total));
      scene.style.setProperty('--fd-progress', progress.toFixed(4));
      scene.classList.toggle('fd-scene--done', progress >= 0.999);
    });

    parallaxEls.forEach(function (el) {
      var factor = parseFloat(el.dataset.fdParallax) || 0.15;
      var rect = el.getBoundingClientRect();
      var center = rect.top + rect.height / 2 - vh / 2;
      el.style.transform = 'translate3d(0,' + (-center * factor).toFixed(1) + 'px,0)';
    });
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  /* ---- Boot -------------------------------------------------------------- */
  function boot(root) {
    initWords(root);
    initReveals(root);
    collect();
    if (!(reduced || designMode)) {
      update();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  if (!(reduced || designMode)) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* Re-init when the theme editor injects/reorders sections */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
